import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Tooltip } from '@mui/material';
import { IssueWorkflowModal } from '@/components/iem/workflows/issue';
import { TurnInWorkflowModal } from '@/components/iem/workflows/turn-in';
import { IEMExchangeModal, type ExchangeCompletionPayload } from '@/components/iem/workflows';
import { CustomerFeedbackModal } from '@/components/feedback/CustomerFeedbackModal';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { useNotifications } from '@/stores/useNotificationStore';
import { useAuth } from '@/hooks/useAuth';
import { iemService } from '@/services/iem';
import { getErrorMessage } from '@/utils/httpClient';
import {
  formatCurrency,
  generateChangeDocument,
  type ChangeDocumentLineItem,
} from '@/utils/generateChangeDocument';
import { preOpenWindow } from '@/utils/pdfHelpers';
import { documentService } from '@/services/documents';
import { useMemberEmail } from '@/hooks/useMemberEmail';
import { logger } from '@equip/utils/logger';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const log = logger.child({ component: 'IemContextualActions' });

export const IemContextualActions: React.FC = () => {
  const { currentCustomer, setCustomer } = useIemFrontDoorStore();
  const { user, isViewer } = useAuth();
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [turnInOpen, setTurnInOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [isExchangeCompleting, setIsExchangeCompleting] = useState(false);
  const exchangeStartedAtRef = useRef('');
  const [pendingFeedbackContext, setPendingFeedbackContext] = useState<{
    transactionLogId: string;
    customerName?: string;
  } | null>(null);
  const [feedbackContext, setFeedbackContext] = useState<{
    transactionLogId: string;
    customerName?: string;
  } | null>(null);
  const { email: memberEmail } = useMemberEmail(currentCustomer?.id);
  const person = usePersonLabels('front_window');

  const isCleared = currentCustomer?.cleared_status === true;
  const disabled = !currentCustomer || isCleared || isViewer;

  const totalItems =
    (currentCustomer?.iem_status?.phr_items_count ?? 0) +
    (currentCustomer?.iem_status?.thr_items_count ?? 0);
  const noItems =
    !!currentCustomer && !isCleared && !isViewer && !!currentCustomer.iem_status && totalItems === 0;
  const turnInDisabled = disabled || noItems;
  const exchangeDisabled = disabled || noItems;

  const unitName = currentCustomer?.unit ?? 'Selected unit';
  const derivedMemberName = currentCustomer
    ? [currentCustomer.rank, currentCustomer.last_name].filter(Boolean).join(' ')
    : undefined;
  const memberDisplay =
    currentCustomer?.display_name ?? derivedMemberName ?? `Selected ${person.lower}`;

  const promptForFeedback = (transactionLogIds?: string[] | null) => {
    if (!transactionLogIds || transactionLogIds.length === 0) {
      return;
    }

    const logId = transactionLogIds.find((id) => typeof id === 'string' && id.trim().length > 0);
    if (!logId) {
      return;
    }

    setPendingFeedbackContext({
      transactionLogId: logId,
      customerName: currentCustomer?.display_name ?? memberDisplay,
    });
  };

  const refreshCurrentCustomer = async () => {
    const individualId = currentCustomer?.individual_id?.trim();
    if (!individualId) {
      return;
    }

    try {
      const refreshed = await iemService.lookupCustomer(individualId);
      setCustomer(refreshed);
    } catch (error) {
      log.warn('Failed to refresh customer after workflow completion', {
        error: String(error),
        individualId,
      });
    }
  };

  useEffect(() => {
    if (!pendingFeedbackContext) {
      return;
    }

    // Prevent overlapping workflow + survey modals, which can trigger focus conflicts.
    if (issueOpen || turnInOpen || exchangeOpen) {
      return;
    }

    if (typeof window === 'undefined') {
      setFeedbackContext(pendingFeedbackContext);
      setPendingFeedbackContext(null);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setFeedbackContext(pendingFeedbackContext);
      setPendingFeedbackContext(null);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [pendingFeedbackContext, issueOpen, turnInOpen, exchangeOpen]);

  const handleExchangeComplete = async (payload: ExchangeCompletionPayload) => {
    if (!currentCustomer) {
      notifications.error(
        'Unable to complete exchange',
        `Select ${person.article} ${person.lower} before completing an exchange.`,
      );
      return;
    }

    const missingHandReceipt = payload.cartLines.find(
      (line) => !line.issuedItem.handReceiptId || !line.issuedItem.assetId,
    );
    if (missingHandReceipt) {
      notifications.error(
        'Missing hand receipt data',
        'One or more cart items are missing receipt or asset details. Refresh and try again.',
      );
      return;
    }

    const missingReplacement = payload.cartLines.find(
      (line) => !line.replacementAssetId || line.replacementAssetId.trim().length === 0,
    );
    if (missingReplacement) {
      notifications.error(
        'Missing replacement asset',
        'Select a replacement asset for each item before completing the exchange.',
      );
      return;
    }

    const missingDisposition = payload.cartLines.find((line) => !line.returnDisposition);
    if (missingDisposition) {
      notifications.error(
        'Select asset condition',
        'Mark each returning asset as serviceable or non-serviceable before completing the exchange.',
      );
      return;
    }

    const items = payload.cartLines.map((line) => ({
      handReceiptId: line.issuedItem.handReceiptId!,
      issuedAssetId: line.issuedItem.assetId!,
      replacementAssetId: line.replacementAssetId,
      returnDisposition: line.returnDisposition!,
    }));

    if (items.length === 0) {
      notifications.warning(
        'Exchange cart is empty',
        'Add exchange pairs before completing the workflow.',
      );
      return;
    }

    setIsExchangeCompleting(true);

    // Pre-open window synchronously while we have user-gesture context
    const exchangeDocWindow = preOpenWindow();

    try {
      const result = await iemService.completeExchange({
        memberId: currentCustomer.id,
        items,
        documentPayload: payload.capturedSignature
          ? {
              signatures: { individual: payload.capturedSignature },
              transactionInfo: {
                timestamp: new Date().toISOString(),
              },
              workflow: 'exchange',
            }
          : undefined,
        startedAt: exchangeStartedAtRef.current || undefined,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['phr-items', currentCustomer.id] }),
        queryClient.invalidateQueries({ queryKey: ['thr-items', currentCustomer.id] }),
        queryClient.invalidateQueries({ queryKey: ['exchange-replacement-options'] }),
      ]);

      notifications.success(
        'Exchange completed',
        result.exchangedCount === 1
          ? 'Swapped one hand receipt item.'
          : `Swapped ${result.exchangedCount} hand receipt items.`,
      );
      notifications.info(
        memberEmail
          ? `Email notification will be sent to ${memberEmail}.`
          : 'Email notification will be sent',
      );

      // Generate Change Document for the exchange
      if (payload.cartLines.length > 0) {
        // Each exchange pair produces two lines: Turn In (old) + Issue (new)
        const changeDocItems: ChangeDocumentLineItem[] = payload.cartLines.flatMap((line) => {
          const receiptType = line.issuedItem.receiptType || 'PHR';
          const rNr = line.returnDisposition === 'recoverable' ? 'R' : 'NR';
          const unitCost = line.issuedItem.unitPrice ?? 0;
          return [
            {
              qty_action: '1', // Exchanges are always 1:1
              lin: line.issuedItem.lin === '—' ? '' : line.issuedItem.lin || '',
              nsn: line.issuedItem.nsn === '—' ? '' : line.issuedItem.nsn || '',
              nomenclature: line.issuedItem.name,
              issue_turn_in: 'Turn In' as const,
              thr_phr: receiptType,
              qty_oh: '',
              unit_cost: unitCost ? formatCurrency(unitCost) : '',
              total_cost: unitCost ? formatCurrency(unitCost) : '',
              r_nr: rNr,
            },
            {
              qty_action: '1',
              lin: line.replacementItem.lin === '—' ? '' : line.replacementItem.lin || '',
              nsn: line.replacementItem.nsn === '—' ? '' : line.replacementItem.nsn || '',
              nomenclature: line.replacementItem.name,
              issue_turn_in: 'Issue' as const,
              thr_phr: receiptType,
              qty_oh: '',
              unit_cost: unitCost ? formatCurrency(unitCost) : '',
              total_cost: unitCost ? formatCurrency(unitCost) : '',
              r_nr: rNr,
            },
          ];
        });

        const changeDocResult = await generateChangeDocument({
          documentType: 'ChangeDocumentExchange',
          items: changeDocItems,
          name:
            [currentCustomer.last_name, currentCustomer.first_name].filter(Boolean).join(', ').trim() ||
            currentCustomer.display_name ||
            memberDisplay,
          individualId: currentCustomer.individual_id,
          rank: currentCustomer.rank,
          section: currentCustomer.unit || unitName,
          documentNumber: result.documentNumber || '',
          transactionId: result.transactionLogIds?.[0],
          signature: payload.capturedSignature || undefined,
          preOpenedWindow: exchangeDocWindow,
        });

        // Link Change Document to transaction log for audit trail consistency
        const exchangeTxLogId = result.transactionLogIds?.[0];
        if (changeDocResult?.documentEntityId && exchangeTxLogId) {
          try {
            await documentService.linkDocumentToTransaction(
              changeDocResult.documentEntityId,
              exchangeTxLogId,
            );
          } catch (linkError) {
            log.error('Failed to link Change Document to exchange transaction', {
              error: String(linkError),
            });
          }
        }

        if (!changeDocResult) {
          notifications.warning('Document Notice', 'Change Document could not be generated.');
        }
      } else if (exchangeDocWindow && !exchangeDocWindow.closed) {
        exchangeDocWindow.close();
      }

      promptForFeedback(result.transactionLogIds);
      setExchangeOpen(false);
    } catch (error) {
      if (exchangeDocWindow && !exchangeDocWindow.closed) exchangeDocWindow.close();
      const message = getErrorMessage(error) || 'Unexpected error completing exchange.';
      notifications.error('Failed to complete exchange', message);
      log.error('Failed to complete exchange', { error: String(error) });
    } finally {
      setIsExchangeCompleting(false);
    }
  };

  return (
    <>
      {isCleared && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          This {person.lower} is cleared. Reactivate them to perform Issue Out, Turn-In, or
          Exchange.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" disabled={disabled} onClick={() => setIssueOpen(true)}>
          Issue Out
        </Button>
        <Tooltip title={noItems ? 'No items to return.' : ''}>
          <span>
            <Button
              variant="contained"
              color="secondary"
              disabled={turnInDisabled}
              onClick={() => setTurnInOpen(true)}
            >
              Turn-In
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={noItems ? 'No items to exchange.' : ''}>
          <span>
            <Button
              variant="outlined"
              disabled={exchangeDisabled}
              onClick={() => { exchangeStartedAtRef.current = new Date().toISOString(); setExchangeOpen(true); }}
            >
              Exchange
            </Button>
          </span>
        </Tooltip>
      </Box>

      <IssueWorkflowModal
        open={issueOpen}
        onClose={() => {
          log.info('Closing IssueWorkflowModal - setting issueOpen to false');
          setIssueOpen(false);
        }}
        unitName={unitName}
        memberName={memberDisplay}
        customerId={currentCustomer?.id}
        memberEmail={memberEmail ?? undefined}
        customer={currentCustomer ?? undefined}
        onCompleted={(result) => {
          promptForFeedback(result.transactionLogIds);
          void refreshCurrentCustomer();
        }}
      />

      <TurnInWorkflowModal
        open={turnInOpen}
        onClose={() => {
          log.info('Closing TurnInWorkflowModal - setting turnInOpen to false');
          setTurnInOpen(false);
        }}
        unitName={unitName}
        memberName={memberDisplay}
        customerId={currentCustomer?.id}
        memberEmail={memberEmail ?? undefined}
        customer={currentCustomer ?? undefined}
        onCompleted={(result) => {
          promptForFeedback(result.transactionLogIds);
          void refreshCurrentCustomer();
        }}
      />

      <IEMExchangeModal
        open={exchangeOpen}
        onClose={() => {
          log.info('Closing IEMExchangeModal - setting exchangeOpen to false');
          setExchangeOpen(false);
        }}
        unitName={unitName}
        memberName={memberDisplay}
        customerId={currentCustomer?.id}
        isCompleting={isExchangeCompleting}
        onCompleteExchange={handleExchangeComplete}
      />

      <CustomerFeedbackModal
        open={Boolean(feedbackContext)}
        transactionLogId={feedbackContext?.transactionLogId}
        customerName={feedbackContext?.customerName}
        userEmail={user?.email}
        onClose={() => setFeedbackContext(null)}
      />
    </>
  );
};
